import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ChevronUp, ChevronDown, X, RefreshCw, Printer, Calendar, Upload, CheckCircle, Paperclip, Eye, Receipt
} from "lucide-react";
import { ReceiptPreviewModal } from "./PaymentReceipt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TX_RATES = { Regular: 0.50, External: 0.75, Internal: 1.00 };

const Spinner = () => <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-300 border-t-[#2F5D8A] rounded-full animate-spin" /></div>;
const MsgBar = ({ message, onClose }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${message.type === "success" ? "bg-[#6B8F62]/10 text-[#6B8F62] border-[#6B8F62]/30" : "bg-[#C07070]/10 text-[#C07070] border-[#C07070]/30"}`}>
    <span className="flex-1">{message.text}</span>
    <button onClick={onClose}><X className="h-3 w-3" /></button>
  </div>
);
const TH = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 text-${align} text-[10px] uppercase text-gray-500 font-semibold tracking-wider`}>{children}</th>
);

const WeeklySummaryModule = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(-1);
  const [expandedInspector, setExpandedInspector] = useState(null);
  const [message, setMessage] = useState(null);
  const [editingPayDate, setEditingPayDate] = useState(null);
  const [payDateValue, setPayDateValue] = useState("");
  const [ordersData, setOrdersData] = useState(null);
  const [drillOrders, setDrillOrders] = useState(null);
  const [paidStatus, setPaidStatus] = useState({});
  const [finalizing, setFinalizing] = useState(null);
  const [receipts, setReceipts] = useState({});
  const [receiptPreview, setReceiptPreview] = useState(null); // { inspector, week_start, week_end }
  const [uploadingReceipt, setUploadingReceipt] = useState(null);

  const loadData = useCallback(async () => {
    try { const res = await axios.get(`${API}/finance/weekly-summary-live?weeks=5`); setData(res.data); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const loadWeekOrders = useCallback(async (ws, we) => {
    try {
      const res = await axios.get(`${API}/finance/orders-by-week`, { params: { week_start: ws, week_end: we } });
      setOrdersData(res.data);
      // Load payment finalization status
      const statusRes = await axios.get(`${API}/finance/inspector-payments-status`, { params: { week_start: ws } });
      const statusMap = {};
      for (const p of (statusRes.data?.payments || [])) {
        statusMap[p.inspector_name] = p;
      }
      setPaidStatus(statusMap);
      // Load receipts
      const rcptRes = await axios.get(`${API}/finance/payment-receipts`, { params: { week_start: ws } });
      const rcptMap = {};
      for (const r of (rcptRes.data?.receipts || [])) {
        rcptMap[r.inspector_name] = r;
      }
      setReceipts(rcptMap);
    } catch { setOrdersData(null); }
  }, []);

  const handleExpandWeek = (i, w) => {
    const next = expandedWeek === i ? -1 : i;
    setExpandedWeek(next);
    setExpandedInspector(null);
    setOrdersData(null);
    setDrillOrders(null);
    if (next >= 0) loadWeekOrders(w.period_start, w.period_end);
  };

  const savePaymentDate = async (ws) => {
    try {
      await axios.patch(`${API}/finance/weekly-payment-date`, { week_start: ws, payment_date: payDateValue });
      setMessage({ type: "success", text: "Payment date updated" });
      setEditingPayDate(null); loadData();
    } catch (e) { setMessage({ type: "error", text: e.response?.data?.detail || "Failed" }); }
  };

  const inspectorData = useMemo(() => {
    if (!ordersData?.orders) return {};
    const map = {};
    for (const o of ordersData.orders) {
      const insp = o.inspector || "Unassigned";
      if (!map[insp]) map[insp] = { orders: [], totalPay: 0, totalReceived: 0, totalTx: 0, discounts: 0, byDay: {}, investigationOrders: 0 };
      const flow = o.flow_type || "Regular";
      const isInvestigation = o.under_investigation;
      // Any adjustment whose adjusted_pay is LESS than original_pay encodes a
      // transmission/Vitória deduction (delta = txFee). Surface the delta in the
      // Tx Fee column and keep the order line at the inspector's gross pay
      // (original_pay), so per-line amounts are never reduced by Vitória.
      // Net payment is computed at totals: total inspector pay − transmission fee.
      const adjOriginalPay = o.has_adjustment ? Number(o.adjustment?.original_pay || 0) : (o.original_inspector_pay || 0);
      const adjAdjustedPay = o.has_adjustment ? Number(o.adjustment?.adjusted_pay || 0) : (o.inspector_pay || 0);
      const adjDelta = Math.max(0, adjOriginalPay - adjAdjustedPay);
      const adjIsTx = o.has_adjustment && adjDelta > 0;
      const txFee = isInvestigation ? 0 : (adjIsTx ? adjDelta : (o.tx_fee || 0));
      const effectivePay = isInvestigation ? 0 : (adjIsTx ? adjOriginalPay : (o.inspector_pay || 0));
      map[insp].orders.push({ ...o, txFee, effectivePay });
      map[insp].totalPay += effectivePay;
      map[insp].totalReceived += (o.received || 0);
      map[insp].totalTx += txFee;
      if (isInvestigation) map[insp].investigationOrders++;
      const day = o.route_date || "Unknown";
      if (!map[insp].byDay[day]) map[insp].byDay[day] = [];
      map[insp].byDay[day].push({ ...o, txFee, effectivePay });
    }
    return map;
  }, [ordersData]);

  const printPaymentDoc = async (w, inspName) => {
    const id = inspectorData[inspName];
    if (!id) return;

    // Fetch operational bonus for Aline
    let operationalPay = 0;
    if (inspName === "MA-ALINESILVA") {
      try {
        const opRes = await axios.get(`${API}/finance/operational-payment`, { params: { week_start: w.period_start } });
        operationalPay = opRes.data?.total_payment || 0;
      } catch {}
    }

    // Check if already paid
    if (paidStatus[inspName]) {
      if (!window.confirm(`${inspName} was already marked as PAID on ${paidStatus[inspName].payment_date}.\n\nReprint document only?`)) return;
      doPrint(w, inspName, id, operationalPay);
      return;
    }

    // Confirm finalization
    const tx = id.totalTx;
    const finalPay = id.totalPay - tx - id.discounts + operationalPay;
    const confirmMsg = inspName === "MA-ALINESILVA"
      ? `FINALIZE PAYMENT for ${inspName}?\n\nInspector Pay: $${id.totalPay.toFixed(2)}\nOperational (orders): $${operationalPay.toFixed(2)}\nTransmission: -$${tx.toFixed(2)}\nNet Pay: $${finalPay.toFixed(2)}\n\nThis will lock the payment and mark as PAID.`
      : `FINALIZE PAYMENT for ${inspName}?\n\nGross: $${id.totalPay.toFixed(2)}\nTransmission: -$${tx.toFixed(2)}\nNet Pay: $${(id.totalPay - tx - id.discounts).toFixed(2)}\n\nThis will lock the payment and mark as PAID.`;
    if (!window.confirm(confirmMsg)) return;

    setFinalizing(inspName);
    try {
      const ordersDetail = id.orders.map(o => ({
        order_number: o.order_number, route_date: o.route_date, flow_type: o.flow_type,
        inspector_pay: o.effectivePay || 0, tx_fee: o.txFee || 0, address: o.address, city: o.city,
      }));
      await axios.post(`${API}/finance/finalize-inspector-payment`, {
        inspector_name: inspName, week_start: w.period_start, week_end: w.period_end,
        gross_pay: id.totalPay, transmission_cost: tx, discounts: id.discounts,
        operational_pay: operationalPay,
        net_pay: finalPay, total_orders: id.orders.length,
        received_from_safeguard: id.totalReceived, orders_detail: ordersDetail,
      });
      setPaidStatus(prev => ({ ...prev, [inspName]: { inspector_name: inspName, net_pay: finalPay, payment_date: new Date().toISOString().slice(0, 10), status: "paid" } }));
      setMessage({ type: "success", text: `${inspName} payment finalized: $${finalPay.toFixed(2)}` });
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to finalize";
      if (detail.includes("already finalized")) {
        setMessage({ type: "error", text: detail });
      } else {
        setMessage({ type: "error", text: detail });
        return;
      }
    } finally {
      setFinalizing(null);
    }
    doPrint(w, inspName, id, operationalPay);
  };

  const doPrint = (w, inspName, id, operationalPay = 0) => {
    const sortedDays = Object.keys(id.byDay).sort();
    const logoUrl = window.location.origin + "/aarifi-logo-full.png";
    const finalPay = id.totalPay - id.totalTx - id.discounts + operationalPay;

    const html = `<!DOCTYPE html><html><head><title>Payment - ${inspName}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;max-width:820px;margin:0 auto;padding:24px;font-size:11px;color:#1F2937}
.header{display:flex;align-items:center;gap:16px;border-bottom:3px solid #1F2937;padding-bottom:14px;margin-bottom:20px}
.header img{height:60px}
.header h1{font-size:20px;letter-spacing:1px}
.header .sub{font-size:10px;color:#6B7280;letter-spacing:2px}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;font-size:11px}
.meta .label{color:#6B7280;font-size:9px;text-transform:uppercase;letter-spacing:1px}
.meta .val{font-weight:700;font-size:13px}
table{width:100%;border-collapse:collapse;margin:6px 0 14px}
th{background:#1F2937;color:#fff;padding:6px 8px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:0.5px}
td{border-bottom:1px solid #E5E7EB;padding:5px 8px}
.day-row td{background:#F3F4F6;font-weight:700;color:#2F5D8A;font-size:10px;border-bottom:2px solid #D1D5DB}
.sub-total td{background:#EBF5FF;font-weight:600;font-size:10px}
.total-section{margin-top:20px;border-top:3px solid #1F2937;padding-top:14px}
.total-row{display:flex;justify-content:space-between;padding:4px 0;font-size:12px}
.total-row.main{font-size:16px;font-weight:800;border-top:2px solid #1F2937;padding-top:8px;margin-top:6px}
.total-row .fee{color:#B08A3E}
.sig-block{margin-top:40px;border-top:1px solid #D1D5DB;padding-top:16px;display:grid;grid-template-columns:1fr 1fr;gap:24px}
.sig-block .line{border-bottom:1px solid #1F2937;height:32px;margin-top:6px}
.sig-block .label{font-size:9px;color:#6B7280;text-transform:uppercase}
.contractor{margin-top:24px;padding:10px;background:#FEF3C7;border:1px solid #D1A054;border-radius:4px;font-size:10px;color:#92400E;text-align:center;font-weight:600}
@media print{body{padding:12px;font-size:10px}.header img{height:50px}}
</style></head><body>
<div class="header">
  <img src="${logoUrl}" alt="AARIFI" onerror="this.style.display='none'"/>
  <div><h1>AARIFI SERVICES LLC</h1><div class="sub">WEEKLY PAYMENT DOCUMENT</div></div>
</div>
<div class="meta">
  <div><div class="label">Inspector</div><div class="val">${inspName}</div></div>
  <div><div class="label">Week Period</div><div class="val">${w.period_start} to ${w.period_end}</div></div>
  <div><div class="label">Payment Date</div><div class="val">${w.payment_date || "TBD"}</div></div>
  <div><div class="label">Total Orders</div><div class="val">${id.orders.length}</div></div>
</div>
<table>
<thead><tr><th>#</th><th>Order Number</th><th>Address</th><th>Type</th><th style="text-align:right">Payment</th><th style="text-align:right">Transmission Fee</th></tr></thead>
<tbody>
${sortedDays.map(day => {
  const dayOrd = id.byDay[day];
  const dayPay = dayOrd.reduce((s, o) => s + (o.effectivePay || 0), 0);
  const dayTx = dayOrd.reduce((s, o) => s + o.txFee, 0);
  return `<tr class="day-row"><td colspan="4">${day} (${dayOrd.length} orders)</td><td style="text-align:right">$${dayPay.toFixed(2)}</td><td style="text-align:right;color:#B08A3E">${dayTx > 0 ? '$'+dayTx.toFixed(2) : '-'}</td></tr>` +
    dayOrd.map((o, i) => `<tr${o.under_investigation ? ' style="background:#FEE2E2;color:#991B1B"' : ''}><td>${i+1}</td><td>${o.order_number}${o.under_investigation ? ' <span style="background:#DC2626;color:white;padding:1px 4px;border-radius:2px;font-size:8px">NOT PAID</span>' : ''}</td><td>${(o.address||'')+', '+(o.city||'')+' '+(o.zip||'')}</td><td>${o.flow_type||''}</td><td style="text-align:right">${o.under_investigation ? '<s>$'+(o.original_inspector_pay||o.inspector_pay||0).toFixed(2)+'</s> $0.00' : '$'+(o.effectivePay||0).toFixed(2)}</td><td style="text-align:right;color:#B08A3E;font-weight:600">${o.txFee > 0 ? '$'+o.txFee.toFixed(2) : '-'}</td></tr>`).join('');
}).join('')}
</tbody></table>
<div class="total-section">
  <div class="total-row"><span>Total Inspector Payment (${id.orders.length} orders)</span><span>$${id.totalPay.toFixed(2)}</span></div>
  <div class="total-row"><span class="fee">Transmission Fee</span><span class="fee">- $${id.totalTx.toFixed(2)}</span></div>
  ${id.discounts > 0 ? `<div class="total-row"><span>Discounts</span><span>- $${id.discounts.toFixed(2)}</span></div>` : ''}
  ${operationalPay > 0 ? `<div class="total-row"><span style="color:#2F5D8A;font-weight:700">Operational Payment (inspector orders)</span><span style="color:#2F5D8A;font-weight:700">+ $${operationalPay.toFixed(2)}</span></div>` : ''}
  <div class="total-row main"><span>FINAL PAYMENT TO INSPECTOR</span><span>$${finalPay.toFixed(2)}</span></div>
</div>
<div class="sig-block">
  <div><div class="label">Inspector Signature</div><div class="line"></div><div style="font-size:10px;margin-top:4px">${inspName}</div></div>
  <div><div class="label">AARIFI Services LLC</div><div class="line"></div><div style="font-size:10px;margin-top:4px">Authorized Representative</div></div>
</div>
<div class="contractor">Independent Contractor (1099) — This payment is for services rendered as an independent contractor.</div>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const handleUploadReceipt = async (file, inspName, weekStart) => {
    setUploadingReceipt(inspName);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("inspector_name", inspName);
      fd.append("week_start", weekStart);
      const res = await axios.post(`${API}/finance/upload-payment-receipt`, fd);
      setReceipts(prev => ({ ...prev, [inspName]: { receipt_url: res.data.url, filename: res.data.filename, uploaded_at: res.data.uploaded_at } }));
      setMessage({ type: "success", text: `Receipt uploaded for ${inspName}` });
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Upload failed" });
    } finally {
      setUploadingReceipt(null);
    }
  };

  if (loading) return <Spinner />;
  const weeks = data?.weeks || [];
  const fmtP = (s, e) => {
    const ds = new Date(s + "T12:00:00"), de = new Date(e + "T12:00:00");
    return `${ds.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${de.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  return (
    <div className="space-y-4" data-testid="weekly-summary-module">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Weekly Payment Summary</p>
          <p className="text-gray-400 text-xs">Inspector Payment + Safeguard Received + Transmission Cost. Click any number to see orders.</p>
        </div>
        <button onClick={loadData} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {message && <MsgBar message={message} onClose={() => setMessage(null)} />}

      {drillOrders && (
        <div className="bg-white rounded-lg border border-[#2F5D8A]/30 p-4 space-y-2" data-testid="drill-orders">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-[#2F5D8A] uppercase">{drillOrders.title} — {drillOrders.list.length} orders</p>
            <button onClick={() => setDrillOrders(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50"><tr className="border-b border-gray-200"><TH>Order #</TH><TH>Inspector</TH><TH>Date</TH><TH>Type</TH><TH align="right">Payment</TH><TH>Address</TH></tr></thead>
              <tbody>{drillOrders.list.map((o, i) => (
                <tr key={i} className="border-b border-gray-100"><td className="px-3 py-1.5 font-medium text-gray-800">{o.order_number}</td><td className="px-3 py-1.5 text-gray-500">{o.inspector}</td><td className="px-3 py-1.5 text-gray-400">{o.route_date}</td><td className="px-3 py-1.5 text-gray-400">{o.flow_type}</td><td className="px-3 py-1.5 text-right font-bold">${(o.inspector_pay||0).toFixed(2)}</td><td className="px-3 py-1.5 text-gray-400 text-[10px]">{o.address}, {o.city}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {weeks.map((w, i) => {
          const expanded = expandedWeek === i;
          const inspList = Object.entries(inspectorData).sort((a, b) => a[0].localeCompare(b[0]));
          const weekTotalPay = inspList.reduce((s, [, d]) => s + d.totalPay, 0);
          const weekTotalRcvd = inspList.reduce((s, [, d]) => s + d.totalReceived, 0);
          const weekTotalTx = inspList.reduce((s, [n, d]) => s + d.totalTx, 0);
          // Aline profit doesn't subtract tx (she absorbs it from her pay)
          const weekProfit = inspList.reduce((s, [n, d]) => {
            const isAlinePay = n === "MA-ALINESILVA";
            return s + (isAlinePay ? (d.totalReceived - d.totalPay) : (d.totalReceived - d.totalPay - d.totalTx));
          }, 0);

          return (
            <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`week-card-${i}`}>
              <button onClick={() => handleExpandWeek(i, w)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left">
                <div className="flex items-center gap-4">
                  <p className="text-sm font-semibold text-[#1F2937]">{fmtP(w.period_start, w.period_end)}</p>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${w.profit >= 0 ? "bg-[#6B8F62]/15 text-[#6B8F62]" : "bg-[#C07070]/15 text-[#C07070]"}`}>
                    {w.profit >= 0 ? "Profit" : "Loss"}: ${Math.abs(w.profit).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-5 text-xs">
                  <span className="text-gray-500">Orders: <span className="font-bold text-[#1F2937]">{w.total_payable}</span></span>
                  <span className="text-gray-500">Insp. Pay: <span className="font-bold text-[#C07070]">${w.total_inspector_cost.toFixed(2)}</span></span>
                  <span className="text-gray-500">Received: <span className="font-bold text-[#6B8F62]">${w.total_received.toFixed(2)}</span></span>
                  {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </button>

              {expanded && (
                <div className="border-t border-gray-200 px-5 py-4 space-y-4">
                  <div className="flex items-center gap-3 bg-[#2F5D8A]/5 rounded-lg px-4 py-2.5 border border-[#2F5D8A]/15">
                    <Calendar className="h-4 w-4 text-[#2F5D8A]" />
                    <span className="text-xs font-semibold text-[#2F5D8A] uppercase">Payment Date:</span>
                    {editingPayDate === i ? (
                      <div className="flex items-center gap-2">
                        <input type="date" value={payDateValue} onChange={e => setPayDateValue(e.target.value)} className="px-2 py-1 border border-[#2F5D8A] rounded text-xs" autoFocus />
                        <button onClick={() => savePaymentDate(w.period_start)} className="px-2 py-1 bg-[#6B8F62] text-white text-[10px] font-bold rounded">Save</button>
                        <button onClick={() => setEditingPayDate(null)} className="px-2 py-1 bg-gray-200 text-gray-500 text-[10px] rounded">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#2F5D8A]">{w.payment_date}</span>
                        <button onClick={(e) => { e.stopPropagation(); setEditingPayDate(i); setPayDateValue(w.payment_date); }} className="px-1.5 py-0.5 text-[9px] font-semibold text-[#2F5D8A] hover:bg-[#2F5D8A]/10 rounded">Edit</button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <button onClick={() => ordersData && setDrillOrders({ title: "All Inspector Payments", list: ordersData.orders })} className="bg-white border border-gray-200 rounded-lg p-3 text-left hover:shadow-md transition-all cursor-pointer" data-testid="card-insp-payment">
                      <p className="text-[10px] uppercase text-gray-500 font-semibold">Inspector Payment</p>
                      <p className="text-xl font-bold text-[#C07070]">${w.total_inspector_cost.toFixed(2)}</p>
                      <p className="text-[9px] text-gray-300">{w.total_payable} orders — click to view</p>
                    </button>
                    <button onClick={() => ordersData && setDrillOrders({ title: "Received from Safeguard", list: ordersData.orders.filter(o => o.received > 0) })} className="bg-white border border-[#6B8F62]/30 rounded-lg p-3 text-left hover:shadow-md transition-all cursor-pointer" data-testid="card-received-sg">
                      <p className="text-[10px] uppercase text-[#6B8F62] font-semibold">Received from Safeguard</p>
                      <p className="text-xl font-bold text-[#6B8F62]">${w.total_received.toFixed(2)}</p>
                      <p className="text-[9px] text-gray-300">Click to view paid orders</p>
                    </button>
                    <div className="bg-white border border-[#B08A3E]/30 rounded-lg p-3" data-testid="card-transmission">
                      <p className="text-[10px] uppercase text-[#B08A3E] font-semibold">Transmission Cost</p>
                      <p className="text-xl font-bold text-[#B08A3E]">${(expanded && inspList.length > 0 ? weekTotalTx : 0).toFixed(2)}</p>
                      <p className="text-[9px] text-gray-300">R=$0.50 | E=$0.75 | I=$1.00</p>
                    </div>
                    <div className={`bg-white border rounded-lg p-3 ${(expanded && inspList.length > 0 ? weekProfit : w.profit) >= 0 ? "border-[#6B8F62]/30" : "border-[#C07070]/30"}`} data-testid="card-profit">
                      <p className="text-[10px] uppercase text-gray-500 font-semibold">Profit</p>
                      <p className={`text-xl font-bold ${(expanded && inspList.length > 0 ? weekProfit : w.profit) >= 0 ? "text-[#6B8F62]" : "text-[#C07070]"}`}>${(expanded && inspList.length > 0 ? weekProfit : w.profit).toFixed(2)}</p>
                      <p className="text-[9px] text-gray-300">Received - Payment - Tx</p>
                    </div>
                  </div>

                  {inspList.length > 0 ? (
                    <div>
                      <p className="text-[10px] uppercase text-gray-500 font-semibold mb-2">Inspector Breakdown (click to expand by day)</p>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead><tr className="border-b border-gray-200 bg-gray-50">
                            <TH>Inspector</TH><TH align="right">Orders</TH><TH align="right">Insp. Payment</TH><TH align="right">Received (SG)</TH><TH align="right">Transmission</TH><TH align="right">Profit</TH><TH align="center">Actions</TH>
                          </tr></thead>
                          <tbody>{inspList.map(([name, id]) => {
                            const tx = id.totalTx;
                            const isAlinePay = name === "MA-ALINESILVA";
                            // Aline: pay = cheque value, tx is deducted from her pay, so profit = received - pay (tx already absorbed)
                            // Others: profit = received - pay - tx (tx is separate cost)
                            const profit = isAlinePay ? (id.totalReceived - id.totalPay) : (id.totalReceived - id.totalPay - tx);
                            const finalPay = id.totalPay - tx;
                            const isExp = expandedInspector === name;
                            const sortedDays = Object.keys(id.byDay).sort().reverse();

                            return (
                              <React.Fragment key={name}>
                                <tr className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${paidStatus[name] ? "bg-[#6B8F62]/[0.03]" : ""}`} onClick={() => setExpandedInspector(isExp ? null : name)} data-testid={`inspector-row-${name}`}>
                                  <td className="px-3 py-2 font-medium text-gray-800 flex items-center gap-1.5">
                                    {isExp ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
                                    {name}
                                    {paidStatus[name] && <span className="ml-1 w-2 h-2 rounded-full bg-[#6B8F62] inline-block" title="Paid" />}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">{id.orders.length}</td>
                                  <td className="px-3 py-2 text-right font-medium text-[#C07070]">${id.totalPay.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-right font-medium text-[#6B8F62]">${id.totalReceived.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-right text-[#B08A3E]">{tx > 0 ? `-$${tx.toFixed(2)}` : "-"}</td>
                                  <td className={`px-3 py-2 text-right font-bold ${profit >= 0 ? "text-[#6B8F62]" : "text-[#C07070]"}`}>${profit.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                    {paidStatus[name] ? (
                                      <>
                                        <span className="px-1.5 py-0.5 bg-[#6B8F62]/15 text-[#6B8F62] rounded text-[8px] font-bold" data-testid={`paid-badge-${name}`}>PAID {paidStatus[name].payment_date}</span>
                                        <button onClick={(e) => { e.stopPropagation(); printPaymentDoc(w, name); }}
                                          className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" title="Reprint">
                                          <Printer className="h-3 w-3" />
                                        </button>
                                      </>
                                    ) : (
                                      <button onClick={(e) => { e.stopPropagation(); printPaymentDoc(w, name); }}
                                        disabled={finalizing === name}
                                        className="px-2 py-1 bg-[#1F2937] hover:bg-[#374151] text-white rounded text-[8px] font-bold disabled:opacity-50"
                                        data-testid={`payment-doc-${name}`}>
                                        <Printer className="h-3 w-3 inline mr-0.5" />{finalizing === name ? "..." : "Print & Pay"}
                                      </button>
                                    )}
                                    {/* 1099-NEC Receipt preview */}
                                    <button onClick={(e) => { e.stopPropagation(); setReceiptPreview({ inspector: name, week_start: w.period_start, week_end: w.period_end }); }}
                                      className="p-1 bg-[#0B2D5B]/10 text-[#0B2D5B] rounded hover:bg-[#0B2D5B]/20 flex items-center"
                                      title="Preview 1099-NEC Payment Receipt"
                                      data-testid={`open-receipt-${name}`}>
                                      <Receipt className="h-3 w-3" />
                                    </button>
                                    {/* Receipt Upload/View */}
                                    {receipts[name] ? (
                                      <a href={receipts[name].receipt_url} target="_blank" rel="noreferrer"
                                        className="p-1 bg-[#6B8F62]/10 text-[#6B8F62] rounded hover:bg-[#6B8F62]/20 flex items-center gap-0.5"
                                        title={`Receipt: ${receipts[name].filename} — Click to view`}
                                        onClick={e => e.stopPropagation()}
                                        data-testid={`receipt-view-${name}`}>
                                        <CheckCircle className="h-3 w-3" />
                                        <Eye className="h-3 w-3" />
                                      </a>
                                    ) : (
                                      <label className={`p-1 rounded cursor-pointer flex items-center ${uploadingReceipt === name ? "bg-[#B08A3E]/10 text-[#B08A3E]" : "text-gray-400 hover:text-[#B08A3E] hover:bg-[#B08A3E]/10"}`}
                                        title="Upload Zelle receipt" onClick={e => e.stopPropagation()}>
                                        {uploadingReceipt === name ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                                        <input type="file" accept="image/*,.pdf" capture="environment" className="hidden"
                                          data-testid={`receipt-upload-${name}`}
                                          onChange={e => e.target.files[0] && handleUploadReceipt(e.target.files[0], name, w.period_start)} />
                                      </label>
                                    )}
                                    </div>
                                  </td>
                                </tr>
                                {isExp && sortedDays.map(day => {
                                  const dayOrd = id.byDay[day];
                                  const dayPay = dayOrd.reduce((s, o) => s + (o.inspector_pay || 0), 0);
                                  const dayTx = dayOrd.reduce((s, o) => s + o.txFee, 0);
                                  return (
                                    <React.Fragment key={`${name}-${day}`}>
                                      <tr className="bg-[#2F5D8A]/5 cursor-pointer" onClick={() => setDrillOrders({ title: `${name} — ${day}`, list: dayOrd })}>
                                        <td className="pl-8 pr-3 py-1.5 font-semibold text-[#2F5D8A] text-[10px]" colSpan={2}>{day} ({dayOrd.length} orders)</td>
                                        <td className="px-3 py-1.5 text-right text-[10px] font-bold text-[#C07070]">${dayPay.toFixed(2)}</td>
                                        <td className="px-3 py-1.5 text-right text-[10px] text-[#6B8F62]">${dayOrd.reduce((s, o) => s + (o.received || 0), 0).toFixed(2)}</td>
                                        <td className="px-3 py-1.5 text-right text-[10px] text-[#B08A3E]">{dayTx > 0 ? `-$${dayTx.toFixed(2)}` : ""}</td>
                                        <td colSpan={2}></td>
                                      </tr>
                                      {dayOrd.map((o, oi) => (
                                        <tr key={`${o.order_number}-${oi}`} className={`border-b border-gray-50 text-[10px] ${o.under_investigation ? "bg-[#DC2626]/8" : o.has_adjustment ? "bg-[#B08A3E]/5" : "bg-gray-50/50"}`}>
                                          <td className="pl-12 pr-3 py-1 text-gray-600">
                                            <span className="flex items-center gap-1">
                                              {o.order_number}
                                              {o.under_investigation && <span className="px-1 py-0 bg-[#DC2626] text-white text-[7px] font-bold rounded">NOT PAID</span>}
                                              {o.has_adjustment && <span className="px-1 py-0 bg-[#B08A3E] text-white text-[7px] font-bold rounded">ADJ</span>}
                                            </span>
                                          </td>
                                          <td className="px-3 py-1 text-right text-gray-400">{o.flow_type}</td>
                                          <td className={`px-3 py-1 text-right ${o.under_investigation ? "text-[#DC2626] line-through" : "text-gray-500"}`}>
                                            ${(o.under_investigation ? 0 : (o.effectivePay ?? o.inspector_pay ?? 0)).toFixed(2)}
                                            {o.under_investigation && <span className="block text-[10px] text-[#B08A3E] italic font-medium">Under investigation</span>}
                                          </td>
                                          <td className="px-3 py-1 text-right text-[#6B8F62]">{o.received > 0 ? `$${o.received.toFixed(2)}` : ""}</td>
                                          <td className="px-3 py-1 text-right text-[#B08A3E]">{o.txFee > 0 ? `$${o.txFee.toFixed(2)}` : ""}</td>
                                          <td colSpan={2} className="px-3 py-1 text-gray-300 truncate max-w-[180px]">{`${o.address}, ${o.city}`}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                                {isExp && sortedDays.length === 0 && (
                                  <tr className="bg-gray-50"><td colSpan={7} className="px-8 py-2 text-gray-400 text-[10px]">Loading orders...</td></tr>
                                )}
                                {isExp && sortedDays.length > 0 && (
                                  <tr className="bg-[#1F2937]/5 border-b border-gray-200">
                                    <td className="pl-8 pr-3 py-2 font-bold text-[#1F2937] text-[10px]" colSpan={2}>TOTAL — {name} ({id.orders.length} orders)</td>
                                    <td className="px-3 py-2 text-right font-bold text-[10px] text-[#C07070]">${id.totalPay.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-bold text-[10px] text-[#6B8F62]">${id.totalReceived.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-bold text-[10px] text-[#B08A3E]">{tx > 0 ? `-$${tx.toFixed(2)}` : ""}</td>
                                    <td className="px-3 py-2 text-right font-bold text-[10px]">Net: ${finalPay.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}</tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-400 text-sm">Loading inspector data...</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 1099-NEC Receipt Preview Modal — placeholder (RC-2 fork: closing truncated file safely) */}
    </div>
  );
};

export default WeeklySummaryModule;
